import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, CheckCircle, Clock, FileText, Sparkles, Zap, Target,
  Layers, Users, Bot, Send, AlertCircle, Play,
  ChevronDown, Save
} from "lucide-react";
import api from "../lib/api";
import { FlowGrid, TraceProgress } from "../components/FlowVisuals";
import { FileUploader, FileList } from "../components/FileUpload";

const MODE_META = {
  quick: { label: "Quick Mode", desc: "AI-first execution. Fastest output.", Icon: Zap, color: "#005CEE" },
  deep: { label: "Deep Mode", desc: "Multi-step structured reasoning.", Icon: Layers, color: "#8A4FFF" },
  manual: { label: "Manual Mode", desc: "Human-led with AI assistance.", Icon: Target, color: "#005CEE" },
  multi_agent: { label: "Multi-Agent", desc: "Parallel research + drafting.", Icon: Users, color: "#8A4FFF" },
};

export default function DoerWorkspace() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draftTitle, setDraftTitle] = useState("");
  const [draftSections, setDraftSections] = useState([{ heading: "", content: "" }]);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState("");
  const [modeOpen, setModeOpen] = useState(false);
  const [taskFiles, setTaskFiles] = useState([]);
  const pollRef = useRef(null);

  const fetchTask = useCallback(async () => {
    try {
      const res = await api.get(`/api/workspace/tasks/${taskId}`);
      setData(res.data);
    } catch (e) {
      if (e.response?.status === 403 || e.response?.status === 404) navigate("/my-work");
    } finally { setLoading(false); }
  }, [taskId, navigate]);

  useEffect(() => {
    fetchTask();
    pollRef.current = setInterval(fetchTask, 5000);
    return () => clearInterval(pollRef.current);
  }, [fetchTask]);

  useEffect(() => {
    if (!taskId) return;
    api.get(`/api/files/list?task_id=${taskId}`).then(r => setTaskFiles(r.data)).catch(() => {});
  }, [taskId]);

  // Pre-fill draft from latest delivery
  useEffect(() => {
    if (data?.deliveries?.[0] && !draftTitle) {
      const d = data.deliveries[0];
      setDraftTitle(d.title || "");
      setDraftSections(d.content_sections?.length ? d.content_sections.map(s => ({ heading: s.heading, content: s.content })) : [{ heading: "", content: "" }]);
    }
  }, [data]);

  const addSection = () => setDraftSections(prev => [...prev, { heading: "", content: "" }]);
  const updateSection = (idx, field, val) => setDraftSections(prev => prev.map((s, i) => i === idx ? { ...s, [field]: val } : s));
  const removeSection = (idx) => setDraftSections(prev => prev.filter((_, i) => i !== idx));

  const submitDraft = async (type = "draft") => {
    if (!draftTitle.trim()) return;
    setSubmitLoading(true);
    try {
      await api.post(`/api/workspace/tasks/${taskId}/delivery`, {
        title: draftTitle,
        sections: draftSections.filter(s => s.heading || s.content),
        delivery_type: type,
      });
      await fetchTask();
    } catch (e) { console.error(e); }
    finally { setSubmitLoading(false); }
  };

  const doAction = async (url, body = {}) => {
    setActionLoading(url);
    try {
      await api.post(url, body);
      await fetchTask();
    } catch (e) { console.error(e); }
    finally { setActionLoading(""); }
  };

  const changeMode = async (mode) => {
    try {
      await api.post(`/api/workspace/tasks/${taskId}/mode`, { mode });
      await fetchTask();
    } catch (e) { console.error(e); }
    setModeOpen(false);
  };

  if (loading) return (
    <div style={{ paddingTop: 120, textAlign: "center" }}>
      <div style={{ width: 48, height: 48, border: "3px solid rgba(59,130,246,0.1)", borderTop: "3px solid var(--accent)", borderRadius: "50%", margin: "0 auto", animation: "spin 0.8s linear infinite" }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  if (!data) return null;

  const { task, stages, activities, deliveries, revisions } = data;
  const modeMeta = MODE_META[task.execution_mode] || MODE_META.quick;
  const ModeIcon = modeMeta.Icon;
  const latestRevision = revisions?.[0];
  const isCompleted = task.status === "completed";
  const isAccepted = task.status === "accepted";
  const isExecuting = ["execution_started", "draft_ready", "review_in_progress", "revision_requested"].includes(task.status);
  const canSubmit = ["execution_started", "revision_requested", "draft_ready"].includes(task.status);

  return (
    <div style={{ paddingTop: 60, minHeight: "100vh", position: "relative" }}>
      <FlowGrid opacity={0.015} />
      {/* Top bar */}
      <div style={{ position: "fixed", top: 60, left: 0, right: 0, zIndex: 50, background: "rgba(248,250,252,0.85)", backdropFilter: "blur(16px)", borderBottom: "1px solid rgba(200,210,225,0.4)", padding: "8px 24px", display: "flex", alignItems: "center", gap: 12 }}>
        <button data-testid="doer-ws-back" onClick={() => navigate("/my-work")} style={{ width: 32, height: 32, borderRadius: 8, border: "1px solid var(--bg-neutral)", background: "white", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <ArrowLeft size={14} color="var(--text-muted)" />
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 700, fontSize: "0.88rem", color: "var(--text-main)" }}>Doer Workspace</div>
          <div style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.62rem", color: "var(--text-muted)" }}>{task.original_query}</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{
            padding: "4px 12px", borderRadius: 100, fontSize: "0.62rem", fontWeight: 700,
            fontFamily: "'Satoshi', sans-serif", letterSpacing: "0.05em",
            background: isCompleted ? "rgba(5,150,105,0.08)" : "var(--accent-subtle)",
            color: isCompleted ? "var(--success)" : "var(--accent-text)",
            border: `1px solid ${isCompleted ? "rgba(5,150,105,0.2)" : "rgba(59,130,246,0.1)"}`,
          }}>
            {task.status?.replace(/_/g, " ").toUpperCase()}
          </div>
          <div onClick={() => setModeOpen(!modeOpen)} style={{ position: "relative", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, padding: "4px 12px", borderRadius: 100, background: `${modeMeta.color}08`, border: `1px solid ${modeMeta.color}20` }}>
            <ModeIcon size={12} color={modeMeta.color} />
            <span style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.62rem", fontWeight: 700, color: modeMeta.color }}>{modeMeta.label}</span>
            <ChevronDown size={10} color={modeMeta.color} />
            {modeOpen && (
              <div style={{ position: "absolute", top: "100%", right: 0, marginTop: 4, background: "white", border: "1px solid rgba(200,210,225,0.5)", borderRadius: 12, padding: 4, boxShadow: "0 8px 32px rgba(0,0,0,0.08)", zIndex: 100, minWidth: 200 }}>
                {Object.entries(MODE_META).map(([key, m]) => (
                  <button key={key} onClick={() => changeMode(key)} style={{ width: "100%", padding: "8px 12px", borderRadius: 8, background: task.execution_mode === key ? `${m.color}08` : "transparent", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 8, textAlign: "left" }}>
                    <m.Icon size={12} color={m.color} />
                    <div>
                      <div style={{ fontSize: "0.72rem", fontWeight: 600, color: "var(--text-main)", fontFamily: "'Satoshi', sans-serif" }}>{m.label}</div>
                      <div style={{ fontSize: "0.55rem", color: "var(--text-muted)", fontFamily: "'Satoshi', sans-serif" }}>{m.desc}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 3-column layout */}
      <div style={{ paddingTop: 52, display: "grid", gridTemplateColumns: "260px 1fr 240px", minHeight: "calc(100vh - 112px)", gap: 0 }}>

        {/* LEFT: Task Brief */}
        <div data-testid="doer-left-panel" style={{ borderRight: "1px solid rgba(200,210,225,0.35)", padding: "20px 16px", overflowY: "auto", maxHeight: "calc(100vh - 112px)" }}>
          <div style={{ fontSize: "0.55rem", fontWeight: 700, color: "var(--text-muted)", fontFamily: "'Satoshi', sans-serif", letterSpacing: "0.1em", marginBottom: 10 }}>TASK BRIEF</div>

          <h2 style={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 700, fontSize: "1rem", color: "var(--text-main)", lineHeight: 1.25, marginBottom: 8 }}>{task.interpreted_goal || task.original_query}</h2>

          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: "0.55rem", fontWeight: 700, color: "var(--text-muted)", fontFamily: "'Satoshi', sans-serif", letterSpacing: "0.08em", marginBottom: 4 }}>ORIGINAL QUERY</div>
            <p style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.78rem", color: "var(--text-secondary)", background: "rgba(255,255,255,0.5)", borderRadius: 8, padding: "8px 10px", border: "1px solid rgba(200,210,225,0.3)" }}>
              "{task.original_query}"
            </p>
          </div>

          <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 14 }}>
            {(task.tags || []).slice(0, 6).map((tag, i) => (
              <span key={i} style={{ fontSize: "0.58rem", fontWeight: 600, padding: "2px 8px", borderRadius: 100, background: "var(--accent-subtle)", color: "var(--accent-text)", border: "1px solid rgba(59,130,246,0.1)", fontFamily: "'Satoshi', sans-serif" }}>
                {tag}
              </span>
            ))}
          </div>

          {/* Constraints */}
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}>
            {[
              { label: "Payout", value: `$${task.estimated_payout?.toLocaleString() || "—"}` },
              { label: "Budget", value: `$${task.budget_min?.toLocaleString()} – $${task.budget_max?.toLocaleString()}` },
              { label: "Timeline", value: `${task.timeframe_min} – ${task.timeframe_max} days` },
              { label: "Category", value: task.category?.replace(/_/g, " ") },
              { label: "Poster", value: task.poster_name || "—" },
            ].map(({ label, value }) => (
              <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: "1px solid rgba(228,234,242,0.2)" }}>
                <span style={{ fontSize: "0.62rem", fontWeight: 600, color: "var(--text-muted)", fontFamily: "'Satoshi', sans-serif", letterSpacing: "0.04em" }}>{label}</span>
                <span style={{ fontSize: "0.68rem", fontWeight: 600, color: "var(--text-main)", fontFamily: "'Satoshi', sans-serif" }}>{value}</span>
              </div>
            ))}
          </div>

          {/* Revision request */}
          {latestRevision && latestRevision.status === "requested" && (
            <div style={{ background: "rgba(220,38,38,0.05)", border: "1px solid rgba(220,38,38,0.15)", borderRadius: 10, padding: "10px 12px", marginBottom: 14 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 4 }}>
                <AlertCircle size={12} color="var(--pink)" />
                <span style={{ fontSize: "0.62rem", fontWeight: 700, color: "var(--pink)", fontFamily: "'Satoshi', sans-serif", letterSpacing: "0.08em" }}>REVISION REQUESTED</span>
              </div>
              <p style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.72rem", color: "var(--text-secondary)" }}>{latestRevision.note || "The poster requested changes."}</p>
            </div>
          )}

          {/* Stage progress */}
          <div style={{ fontSize: "0.55rem", fontWeight: 700, color: "var(--text-muted)", fontFamily: "'Satoshi', sans-serif", letterSpacing: "0.1em", marginBottom: 8 }}>PROGRESS</div>
          <TraceProgress stages={stages} small />
        </div>

        {/* CENTER: Execution Canvas */}
        <div data-testid="doer-center-panel" style={{ padding: "20px 28px", overflowY: "auto", maxHeight: "calc(100vh - 112px)" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <FileText size={15} color="var(--accent)" strokeWidth={1.5} />
              <h3 style={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 700, fontSize: "1rem", color: "var(--text-main)" }}>Execution Canvas</h3>
            </div>
          </div>

          {/* Start Execution CTA (when task is just accepted) */}
          {isAccepted && (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              style={{ textAlign: "center", padding: "3rem 2rem", background: "rgba(255,255,255,0.5)", border: "1px solid rgba(200,210,225,0.35)", borderRadius: 16 }}>
              <motion.div animate={{ scale: [1, 1.05, 1] }} transition={{ duration: 2, repeat: Infinity }}
                style={{ width: 64, height: 64, borderRadius: 20, background: "linear-gradient(135deg, var(--accent-subtle), rgba(139,92,246,0.06))", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", boxShadow: "0 8px 32px rgba(59,130,246,0.08)" }}>
                <Play size={24} color="var(--accent)" strokeWidth={1.5} />
              </motion.div>
              <h3 style={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 700, fontSize: "1.15rem", color: "var(--text-main)", marginBottom: 8 }}>Task accepted</h3>
              <p style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.85rem", color: "var(--text-muted)", maxWidth: 400, margin: "0 auto 20px", lineHeight: 1.5 }}>
                Review the task brief, then start execution to unlock the delivery canvas.
              </p>
              <button data-testid="start-execution-btn" onClick={() => doAction(`/api/workspace/tasks/${taskId}/start`)}
                disabled={actionLoading !== ""}
                className="btn-primary" style={{ padding: "0.65rem 2rem", fontSize: "0.88rem", display: "inline-flex", alignItems: "center", gap: 8, opacity: actionLoading ? 0.5 : 1 }}>
                <Play size={14} /> Start Execution
              </button>
            </motion.div>
          )}

          {isCompleted ? (
            <div style={{ textAlign: "center", padding: "3rem 0" }}>
              <CheckCircle size={40} color="var(--success)" style={{ margin: "0 auto 12px" }} />
              <p style={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 700, fontSize: "1.1rem", color: "var(--success)" }}>Task completed</p>
              <p style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.82rem", color: "var(--text-muted)" }}>This task has been approved and finalized.</p>
            </div>
          ) : isExecuting || canSubmit ? (
            <>
              {/* Draft builder */}
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: "0.62rem", fontWeight: 700, color: "var(--text-muted)", fontFamily: "'Satoshi', sans-serif", letterSpacing: "0.08em", marginBottom: 4, display: "block" }}>DELIVERY TITLE</label>
                <input data-testid="draft-title" className="form-input" value={draftTitle} onChange={e => setDraftTitle(e.target.value)} placeholder="Title for your delivery" style={{ fontSize: "0.88rem", fontWeight: 600 }} />
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {draftSections.map((section, i) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                    style={{ background: "rgba(255,255,255,0.5)", border: "1px solid rgba(200,210,225,0.35)", borderRadius: 14, padding: "14px 16px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                      <span style={{ fontSize: "0.55rem", fontWeight: 700, color: "var(--text-muted)", fontFamily: "'Satoshi', sans-serif", letterSpacing: "0.08em" }}>SECTION {i + 1}</span>
                      {draftSections.length > 1 && (
                        <button onClick={() => removeSection(i)} style={{ fontSize: "0.6rem", color: "var(--text-muted)", background: "none", border: "none", cursor: "pointer", fontFamily: "'Satoshi', sans-serif" }}>Remove</button>
                      )}
                    </div>
                    <input className="form-input" value={section.heading} onChange={e => updateSection(i, "heading", e.target.value)} placeholder="Section heading" style={{ marginBottom: 8, fontSize: "0.82rem", fontWeight: 600 }} />
                    <textarea className="form-input" value={section.content} onChange={e => updateSection(i, "content", e.target.value)} placeholder="Section content..." rows={4} style={{ resize: "vertical", fontSize: "0.82rem", lineHeight: 1.6 }} />
                  </motion.div>
                ))}
              </div>

              <button onClick={addSection} style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 4, padding: "6px 12px", borderRadius: 8, background: "transparent", border: "1px dashed rgba(200,210,225,0.5)", cursor: "pointer", fontFamily: "'Satoshi', sans-serif", fontSize: "0.72rem", color: "var(--text-muted)" }}>
                + Add section
              </button>
            </>
          ) : null}
        </div>

        {/* RIGHT: Actions */}
        <div data-testid="doer-right-panel" style={{ borderLeft: "1px solid rgba(200,210,225,0.35)", padding: "20px 16px", overflowY: "auto", maxHeight: "calc(100vh - 112px)" }}>

          {canSubmit && (
            <>
              <div style={{ fontSize: "0.55rem", fontWeight: 700, color: "var(--text-muted)", fontFamily: "'Satoshi', sans-serif", letterSpacing: "0.1em", marginBottom: 10 }}>SUBMIT</div>

              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
                <button data-testid="submit-draft-btn" onClick={() => submitDraft("draft")} disabled={submitLoading || !draftTitle.trim()}
                  style={{ width: "100%", padding: "0.55rem", borderRadius: 10, background: "var(--accent)", color: "white", border: "none", fontFamily: "'Satoshi', sans-serif", fontWeight: 700, fontSize: "0.78rem", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, opacity: submitLoading || !draftTitle.trim() ? 0.5 : 1 }}>
                  <Send size={12} /> {submitLoading ? "Submitting..." : "Submit draft"}
                </button>
                <button data-testid="submit-final-btn" onClick={() => submitDraft("final")} disabled={submitLoading || !draftTitle.trim()}
                  style={{ width: "100%", padding: "0.55rem", borderRadius: 10, background: "var(--success)", color: "white", border: "none", fontFamily: "'Satoshi', sans-serif", fontWeight: 700, fontSize: "0.78rem", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, opacity: submitLoading || !draftTitle.trim() ? 0.5 : 1 }}>
                  <CheckCircle size={12} /> Submit final
                </button>
              </div>

              <div style={{ fontSize: "0.55rem", fontWeight: 700, color: "var(--text-muted)", fontFamily: "'Satoshi', sans-serif", letterSpacing: "0.1em", marginBottom: 8 }}>QUICK ACTIONS</div>
              {["Generate draft", "Improve draft", "Expand content", "Refine & polish"].map((action, i) => (
                <button key={i} style={{ width: "100%", padding: "6px 10px", borderRadius: 8, marginBottom: 4, background: "rgba(255,255,255,0.5)", border: "1px solid rgba(200,210,225,0.3)", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, fontFamily: "'Satoshi', sans-serif", fontSize: "0.7rem", color: "var(--text-secondary)", transition: "all 200ms ease", textAlign: "left" }}
                  onMouseEnter={e => { e.currentTarget.style.background = "var(--accent-subtle)"; e.currentTarget.style.borderColor = "rgba(59,130,246,0.15)"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.5)"; e.currentTarget.style.borderColor = "rgba(200,210,225,0.3)"; }}>
                  <Sparkles size={11} color="var(--accent)" /> {action}
                </button>
              ))}
            </>
          )}

          {isAccepted && (
            <div style={{ padding: "16px 14px", borderRadius: 12, background: "var(--accent-subtle)", border: "1px solid rgba(59,130,246,0.1)", textAlign: "center" }}>
              <Play size={18} color="var(--accent)" style={{ margin: "0 auto 6px" }} />
              <p style={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 600, fontSize: "0.82rem", color: "var(--accent-text)", marginBottom: 4 }}>Ready to start</p>
              <p style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.72rem", color: "var(--text-muted)" }}>
                Click "Start Execution" in the canvas to begin working.
              </p>
            </div>
          )}

          {isCompleted && (
            <div style={{ padding: "16px 14px", borderRadius: 12, background: "rgba(5,150,105,0.06)", border: "1px solid rgba(5,150,105,0.15)", textAlign: "center" }}>
              <CheckCircle size={18} color="var(--success)" style={{ margin: "0 auto 6px" }} />
              <p style={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 600, fontSize: "0.82rem", color: "var(--success)" }}>Task completed</p>
            </div>
          )}

          {/* Deliverable files */}
          <div style={{ marginTop: 20 }}>
            <div style={{ fontSize: "0.55rem", fontWeight: 700, color: "var(--text-muted)", fontFamily: "'Satoshi', sans-serif", letterSpacing: "0.1em", marginBottom: 8 }}>DELIVERABLES</div>
            <FileUploader taskId={taskId} onUpload={(f) => setTaskFiles(prev => [f, ...prev])} />
            <div style={{ marginTop: 8 }}>
              <FileList files={taskFiles} onDelete={async (id) => { try { await api.delete(`/api/files/${id}`); setTaskFiles(prev => prev.filter(f => f.id !== id)); } catch {} }} />
            </div>
          </div>

          {/* Activity feed */}
          <div style={{ marginTop: 20 }}>
            <div style={{ fontSize: "0.55rem", fontWeight: 700, color: "var(--text-muted)", fontFamily: "'Satoshi', sans-serif", letterSpacing: "0.1em", marginBottom: 8 }}>ACTIVITY</div>
            {(data.activities || []).slice(0, 8).map((act, i) => (
              <div key={act.id || i} style={{ display: "flex", gap: 6, alignItems: "flex-start", marginBottom: 6 }}>
                <div style={{ width: 5, height: 5, borderRadius: "50%", marginTop: 5, flexShrink: 0, background: act.event_type === "delivery" ? "var(--accent)" : act.event_type === "revision" ? "var(--pink)" : "var(--bg-neutral)" }} />
                <div>
                  <p style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.65rem", color: "var(--text-secondary)", lineHeight: 1.35 }}>{act.message}</p>
                  <p style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.5rem", color: "var(--text-muted)" }}>{new Date(act.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
